import CardCommon from '@op/components/CardCommon';
import { useHashtagTrend } from '@opm/pages/Hashtag/hooks';
import React, { useMemo, useState } from 'react';
import TrendFilter from './TrendFilter';
import EChartCommon from '@op/components/EChartCommon';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { fillNullWithZero } from '@op/pages/Overview/RealtimeOpinions/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { echartsContent } from '@op/utils';
import useIntls from '@opm/hooks/useIntls';
import { gotoDownload } from '@opm/pages/Download';

interface IHashtagTrendProps {
  filter: any;
  firstAvailableTime?: string;
}

export default function HashtagTrend(props: IHashtagTrendProps) {
  const { filter, firstAvailableTime } = props;
  const [trendFilter, setTrendFilter] = useState<any>(null);
  const { intl } = useIntls();
  const { loading, data } = useHashtagTrend({
    filter: trendFilter,
  });

  const changeFilter = (val: any) => {
    setTrendFilter({ ...(filter || {}), ...val });
  };

  const dealWithKeyValue = (key: string, valueKey?: 'label' | 'color') => {
    const keyMaps: Record<
    string,
    {
      label: string;
      color: string;
      type: 'bar' | 'line';
      yAxisIndex: number;
    }
    > = {
      tweets_views: {
        label: 'TikTok Views',
        color: '#5086F3',
        type: 'bar',
        yAxisIndex: 0,
      },
      tweets_posts: {
        label: 'TikTok Videos',
        color: '#69D19C',
        type: 'line',
        yAxisIndex: 1,
      },
    };
    const selected = keyMaps[key];
    if (valueKey) {
      return selected[valueKey];
    }

    return selected;
  };

  const options = useMemo(() => {
    const { series: seriesData, legend, xAxis } = data || {};
    const series = (seriesData || []).map((val: any) => {
      const { name, data: valData } = val;
      const seriesInfo = dealWithKeyValue(name) as object;
      const item = { ...val };

      return {
        ...item,
        data: fillNullWithZero(valData),
        ...(seriesInfo || {}),
      };
    });

    const options = {
      legend: {
        data: legend,
        formatter: (params: string) => {
          const label = dealWithKeyValue(params, 'label') as string;
          return intl(label);
        },
      },
      xAxis: {
        data: xAxis,
        axisLabel: getXAxisAxisLabel(filter),
      },
      grid: {
        left: 10,
        right: 10,
      },
      series,
      tooltip: {
        className: 'dbo-mentions-sentiment-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(
            params[0]?.axisValue,
            filter?.date_type || 'hourly',
          );
          return formatChartParamsDate({
            title: leftTitle,
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const label = dealWithKeyValue(
                item.seriesName,
                'label',
              ) as string;
              return intl({ id: label });
            },
          });
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
        },
      ],
    };

    return options;
  }, [data, intl]);

  const num = filter?.time_range === 'last_7_days' ? 7 : 30;

  return (
    <CardCommon
      title={`${intl({ id: 'Hashtag Trend (By Last {num} days)' }, { num })}`}
      className="dbom-hashtag-trend"
      loading={loading}
      hasDownload
      subFilterBeforeElement={
        <TrendFilter
          filterValue={filter}
          instanceId='hashtag_trend'
          change={changeFilter}
          num={num}
          firstAvailableTime={firstAvailableTime}
        />
      }
      handleDownloadOnClick={() => {
        gotoDownload({
          key: 'HashtagTrendDownload',
          fileName: `Hashtag Trend (By Last ${num} days)`,
          data: data?.timeline,
          filter,
        });
      }}
      empty={data?.isEmpty}
      loadingHeight={620}
      emptyHeight={620}
    >
      <EChartCommon
        optionsProps={{ type: 'bar' }}
        options={options}
        height={620}
      />
    </CardCommon>
  );
}
